/**
 * LooperNode - Iterative Processing Workflow Node
 *
 * Inspired by Google Breadboard's Looper pattern, this node iterates over
 * a list of items, executing a sub-board or task for each one.
 *
 * Features:
 * - Connect a list output to the "items" input handle
 * - Configure what task to perform on each item
 * - Visual progress bar showing iteration status
 * - Pause on failure with skip/retry/abort options
 * - Preview of selected sub-board to execute
 *
 * Design follows the same patterns as GeneratorNode:
 * - Multi-layered shadows for depth
 * - Floating title header on hover
 * - Status-colored connection handles
 * - Consistent spacing and typography
 */

import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';
import type { LooperNodeData, FailureAction, LooperConfig } from '@deep-agents/shared-types';
import { ProgressBar } from './ProgressBar';
import { SubBoardPreview } from './SubBoardPreview';

// ============================================================================
// TYPES
// ============================================================================

type LooperNodeType = Node<LooperNodeData, 'looper'>;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * LooperNode Component
 *
 * Props come from React Flow's node data structure.
 * The node receives items via connection and iterates over them.
 */
export const LooperNode = memo(({ id, data, selected, dragging }: NodeProps<LooperNodeType>) => {
  // ==================== DESTRUCTURE DATA ====================
  const {
    title = 'Looper',
    config,
    state,
    items = [],
    onTitleChange,
    onConfigChange,
    onStart,
    onPause,
    onResume,
  } = data;

  // ==================== LOCAL STATE ====================

  // Local title state (synced to parent on change)
  const [localTitle, setLocalTitle] = useState(title);

  // Local task description (synced to parent on change)
  const [taskDescription, setTaskDescription] = useState(config?.task || '');

  // Hover state for showing/hiding controls
  const [isHovered, setIsHovered] = useState(false);

  // ==================== SYNC EFFECTS ====================

  // Sync local state with props when they change (from Yjs updates)
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  useEffect(() => {
    setTaskDescription(config?.task || '');
  }, [config?.task]);

  // ==================== SHADOW STYLE ====================

  /**
   * Get shadow style based on node state
   * Matches all other nodes for consistency
   */
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // ==================== HANDLERS ====================

  /**
   * Handle title changes
   */
  const handleTitleChange = useCallback((newTitle: string) => {
    setLocalTitle(newTitle);
    onTitleChange?.(newTitle);
  }, [onTitleChange]);

  /**
   * Handle task description changes
   */
  const handleTaskChange = useCallback((newTask: string) => {
    setTaskDescription(newTask);
    const updatedConfig: LooperConfig = {
      task: newTask,
      subBoardId: config?.subBoardId,
      sequential: config?.sequential ?? true,
    };
    onConfigChange?.(updatedConfig);
  }, [config, onConfigChange]);

  /**
   * Handle start button click
   */
  const handleStart = useCallback(() => {
    onStart?.();
  }, [onStart]);

  /**
   * Handle failure action selection (skip/retry/abort)
   */
  const handleFailureAction = useCallback((action: FailureAction) => {
    onResume?.(action);
  }, [onResume]);

  // ==================== COMPUTED VALUES ====================

  // Show controls when hovered OR selected
  const showControls = isHovered || selected;

  // Get current item label for progress display
  const getCurrentItemLabel = (): string | undefined => {
    if (state?.status !== 'executing') return undefined;
    const currentItem = items[state?.currentIndex || 0];
    if (!currentItem) return undefined;
    // Try to get a readable label from the item
    if (typeof currentItem === 'string') return currentItem;
    if (typeof currentItem === 'object' && currentItem !== null) {
      const obj = currentItem as Record<string, unknown>;
      if (obj.name) return String(obj.name);
      if (obj.title) return String(obj.title);
      if (obj.id) return String(obj.id);
    }
    return `Item ${(state?.currentIndex || 0) + 1}`;
  };

  // ==================== RENDER ====================

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ==================== CONNECTION HANDLES ==================== */}
      {/* Left handles for inputs */}
      <Handle
        type="target"
        position={Position.Left}
        id="items"
        className="!w-4 !h-4 !bg-amber-400 !border-2 !border-white !opacity-100"
        style={{ top: '30%', pointerEvents: 'all', zIndex: 100 }}
        title="Connect list input"
      />
      <Handle
        type="target"
        position={Position.Left}
        id="config"
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-white !opacity-100"
        style={{ top: '70%', pointerEvents: 'all', zIndex: 100 }}
        title="Configuration input"
      />
      {/* Right handle for outputs */}
      <Handle
        type="source"
        position={Position.Right}
        id="outputs"
        className="!w-4 !h-4 !bg-green-500 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Collected outputs"
      />

      {/* ==================== FLOATING HEADER ==================== */}
      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="flex items-center gap-2 px-1 nodrag nopan">
          <input
            type="text"
            value={localTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Untitled Looper"
            className="
              px-2.5 py-1 text-[12px] font-semibold
              bg-gradient-to-r from-amber-50 to-orange-50
              rounded-lg border border-amber-100/50
              outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-200
              placeholder:text-amber-300 text-amber-700
            "
          />
        </div>
      </div>

      {/* ==================== MAIN CARD ==================== */}
      <div
        className="
          relative bg-white rounded-2xl overflow-visible
          border border-neutral-200/60 transition-all duration-300
        "
        style={{
          boxShadow: getShadowStyle(),
          minWidth: 360,
          minHeight: 280,
        }}
      >
        {/* Resize handles */}
        <NodeResizer
          minWidth={360}
          minHeight={280}
          isVisible={selected}
          lineClassName="border-amber-500"
          handleClassName="bg-amber-500 border-2 border-white rounded"
        />

        <div className="px-5 pt-5 pb-4">
          {/* ==================== TASK DESCRIPTION ==================== */}
          <div className="mb-4 nodrag nopan">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Task Description
            </label>
            <textarea
              value={taskDescription}
              onChange={(e) => handleTaskChange(e.target.value)}
              placeholder="Describe what to do with each item..."
              className="
                w-full px-3 py-2 text-[14px] bg-gray-50 border border-gray-200
                rounded-lg outline-none focus:border-amber-300 focus:ring-2 focus:ring-amber-100
                resize-none min-h-[60px]
              "
            />
          </div>

          {/* ==================== ITEMS PREVIEW ==================== */}
          <div className="mb-4 bg-amber-50/50 rounded-xl p-3 border border-amber-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">
                Input Items
              </span>
              <span className="text-[11px] text-amber-600 font-medium">
                {items.length} items
              </span>
            </div>
            {items.length === 0 ? (
              <div className="text-xs text-amber-400">
                Connect a list to the items input
              </div>
            ) : (
              <div className="text-xs text-amber-600 truncate">
                First: {JSON.stringify(items[0]).slice(0, 50)}...
              </div>
            )}
          </div>

          {/* ==================== PROGRESS BAR ==================== */}
          <div className="mb-4">
            <ProgressBar
              current={state?.currentIndex || 0}
              total={state?.totalItems || items.length}
              status={state?.status || 'idle'}
              currentItemLabel={getCurrentItemLabel()}
            />
          </div>

          {/* ==================== FAILURE ACTION BUTTONS ==================== */}
          {state?.status === 'paused' && state.failedItem && (
            <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200 nodrag nopan">
              <div className="text-xs text-yellow-700 mb-2">
                <strong>Failed on item {state.failedItem.index + 1}:</strong>
                <br />
                {state.failedItem.error}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleFailureAction('skip')}
                  className="px-3 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded cursor-pointer"
                >
                  Skip
                </button>
                <button
                  onClick={() => handleFailureAction('retry')}
                  className="px-3 py-1 text-xs font-medium bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded cursor-pointer"
                >
                  Retry
                </button>
                <button
                  onClick={() => handleFailureAction('abort')}
                  className="px-3 py-1 text-xs font-medium bg-red-100 hover:bg-red-200 text-red-700 rounded cursor-pointer"
                >
                  Abort
                </button>
              </div>
            </div>
          )}

          {/* ==================== SUB-BOARD PREVIEW ==================== */}
          <div className="mb-4">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Execute Per Item
            </label>
            <SubBoardPreview
              boardId={config?.subBoardId}
              onSelect={() => {
                // TODO: Open board selector modal
                console.log('Select sub-board');
              }}
            />
          </div>

          {/* ==================== START/PAUSE BUTTON ==================== */}
          <div className="nodrag nopan">
            {state?.status === 'executing' ? (
              <button
                onClick={onPause}
                className="
                  w-full flex items-center justify-center gap-2 px-4 py-2.5
                  text-[14px] font-semibold rounded-lg
                  bg-yellow-500 text-white hover:bg-yellow-600 transition-all cursor-pointer
                "
              >
                {/* Pause icon */}
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
                Pause
              </button>
            ) : (
              <button
                onClick={handleStart}
                disabled={items.length === 0 || state?.status === 'complete'}
                className={`
                  w-full flex items-center justify-center gap-2 px-4 py-2.5
                  text-[14px] font-semibold rounded-lg transition-all
                  ${items.length === 0 || state?.status === 'complete'
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-md hover:shadow-lg cursor-pointer'
                  }
                `}
              >
                {/* Play icon */}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {state?.status === 'complete' ? 'Complete' : 'Start Loop'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// Display name for React DevTools
LooperNode.displayName = 'LooperNode';
